import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { Camera, RefreshCw, Check, Loader2, Heart } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const MAX_ATTEMPTS = 3;

export default function RegistrationScreen({ onComplete }) {
  // step: "camera" | "preview" | "checking" | "name" | "saving"
  const [step, setStep]               = useState("camera");
  const [capturedBlob, setCapturedBlob] = useState(null);
  const [capturedUrl, setCapturedUrl]   = useState(null);
  const [name, setName]               = useState("");
  const [error, setError]             = useState("");
  const [attempts, setAttempts]       = useState(0);
  const [saving, setSaving]           = useState(false);
  const [cameraReady, setCameraReady] = useState(false);

  const videoRef  = useRef(null);
  const streamRef = useRef(null);

  const startCamera = useCallback(async () => {
    setCameraReady(false);
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => setCameraReady(true);
      }
    } catch {
      setError("Camera access denied. Please allow camera access and reload the page.");
    }
  }, []);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    startCamera();
    return stopCamera;
  }, [startCamera, stopCamera]);

  const capture = () => {
    const video  = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    // Mirror the frame (front camera is naturally mirrored in CSS but raw pixels aren't)
    const ctx = canvas.getContext("2d");
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        setCapturedBlob(blob);
        setCapturedUrl(URL.createObjectURL(blob));
        stopCamera();
        setStep("preview");
      },
      "image/jpeg",
      0.92,
    );
  };

  const retake = () => {
    if (capturedUrl) URL.revokeObjectURL(capturedUrl);
    setCapturedBlob(null);
    setCapturedUrl(null);
    setError("");
    setStep("camera");
    startCamera();
  };

  const checkFace = async () => {
    setStep("checking");
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", capturedBlob, "selfie.jpg");
      const { data } = await axios.post(`${API}/check-face`, fd);

      if (data.faceDetected) {
        setStep("name");
      } else {
        const next = attempts + 1;
        setAttempts(next);
        if (next >= MAX_ATTEMPTS) {
          setError(
            `Couldn't detect a face after ${MAX_ATTEMPTS} tries. Make sure you're in good light, facing the camera, and try again.`,
          );
          setAttempts(0);
        } else {
          setError(`No face detected. ${MAX_ATTEMPTS - next} attempt${MAX_ATTEMPTS - next === 1 ? "" : "s"} left.`);
        }
        setStep("preview");
      }
    } catch {
      setError("Something went wrong. Please try again.");
      setStep("preview");
    }
  };

  const register = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", capturedBlob, "selfie.jpg");
      fd.append("userName", name.trim());
      const { data } = await axios.post(`${API}/register`, fd);
      onComplete({ userName: data.userName, selfieUrl: data.selfieUrl });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Registration failed. Please try again.";
      setError(msg);
      setSaving(false);
    }
  };

  return (
    <div className="reg-overlay">
      <div className="reg-card">
        {/* Header */}
        <div className="reg-header">
          <h1 className="reg-title">
            PaRi<Heart size={20} fill="#DC2626" stroke="none" className="reg-heart" />Tale
          </h1>
          <p className="reg-hashtag">#PaRiTaleWedding</p>
        </div>

        {/* ── Step: Camera ── */}
        {step === "camera" && (
          <>
            <p className="reg-hint">Take a quick selfie to join the celebration</p>
            <div className="reg-camera-wrap">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="reg-video"
              />
              {!cameraReady && !error && (
                <div className="reg-camera-loading">
                  <Loader2 size={28} className="spinning" />
                </div>
              )}
              <div className="reg-camera-ring" />
            </div>
            {error && <p className="reg-error">{error}</p>}
            <button
              className="reg-btn primary"
              onClick={capture}
              disabled={!cameraReady}
            >
              <Camera size={18} />
              Take Selfie
            </button>
          </>
        )}

        {/* ── Step: Preview / Checking ── */}
        {(step === "preview" || step === "checking") && capturedUrl && (
          <>
            <p className="reg-hint">
              {step === "checking" ? "Verifying your face…" : "Looking good?"}
            </p>
            <div className="reg-preview-wrap">
              <img src={capturedUrl} alt="selfie" className="reg-preview-img" />
              {step === "checking" && (
                <div className="reg-checking-overlay">
                  <Loader2 size={36} className="spinning" />
                </div>
              )}
            </div>
            {error && <p className="reg-error">{error}</p>}
            {step === "preview" && (
              <div className="reg-btn-row">
                <button className="reg-btn secondary" onClick={retake}>
                  <RefreshCw size={16} />
                  Retake
                </button>
                <button className="reg-btn primary" onClick={checkFace}>
                  <Check size={16} />
                  Looks Good
                </button>
              </div>
            )}
          </>
        )}

        {/* ── Step: Name entry ── */}
        {step === "name" && (
          <>
            <img src={capturedUrl} alt="selfie" className="reg-selfie-thumb" />
            <p className="reg-hint">Great photo! What's your name?</p>
            <input
              className="reg-name-input"
              type="text"
              placeholder="Enter your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !saving && register()}
              autoFocus
              maxLength={40}
            />
            {error && <p className="reg-error">{error}</p>}
            <button
              className="reg-btn primary"
              onClick={register}
              disabled={!name.trim() || saving}
            >
              {saving
                ? <><Loader2 size={16} className="spinning" /> Saving…</>
                : "Join the celebration 🎉"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
